import { stopDisplayState } from '../../../utils/composeDriverRecords';

// The driver day view's model: one person's whole shift across every route
// they touched. Route records are route-shaped, so a driver who picked up
// part of someone else's route appears in several of them; this pulls their
// own stops back together, in the order they worked them.
//
// A stop belongs to a driver when they stocked it, or when they have scanned
// in and not finished — otherwise a driver mid-shift would look done.

function stopsOn(record, email) {
  return (record.routeStops || [])
    .filter(
      (stop) =>
        stop.stockedBy === email ||
        (stop.state === 'current' && stop.scannedBy === email),
    )
    .map((stop) => ({
      ...stop,
      routeId: record.routeId,
      routeName: record.routeName || record.routeId,
      // The route's own stop count, for the popup's "Stop N of T".
      routeStopsTotal: record.stopsTotal,
    }));
}

// Visit order: when the stop was stocked, else when it was scanned into;
// ties (and untimed stops, which sort last) fall back to route then number.
const byVisitTime = (stopA, stopB) => {
  const timeA = stopA.stockedAtMs ?? stopA.scannedAtMs ?? Infinity;
  const timeB = stopB.stockedAtMs ?? stopB.scannedAtMs ?? Infinity;
  if (timeA !== timeB) {
    return timeA - timeB;
  }
  const byRoute = (stopA.routeId || '').localeCompare(stopB.routeId || '');
  return byRoute !== 0
    ? byRoute
    : (stopA.stopIndex || 0) - (stopB.stopIndex || 0);
};

/**
 * Build one driver's day from the composed route records.
 *
 * @param {Array} records composed driver records (route-shaped)
 * @param {string|null} email the driver, any case
 * @returns {{
 *   email: string,
 *   name: string,
 *   stops: Array,
 *   routes: Array<{routeId: string, routeName: string, status: string,
 *     stopsHere: number}>,
 *   stopsServiced: number,
 *   currentStop: Object|null,
 * }|null} null with no driver, or one nothing is recorded for.
 */
export default function buildDriverDay(records, email) {
  if (!email) {
    return null;
  }
  const target = email.toLowerCase();
  const stops = [];
  const routes = [];
  let name = '';

  (records || []).forEach((record) => {
    const rosterEntry = (record.driverRoster || []).find(
      (entry) => entry.email === target,
    );
    if (rosterEntry && !name) {
      name = rosterEntry.name;
    }
    const mine = stopsOn(record, target);
    if (mine.length === 0) {
      return;
    }
    stops.push(...mine);
    routes.push({
      routeId: record.routeId,
      routeName: record.routeName || record.routeId,
      status: record.status,
      stopsHere: mine.length,
    });
  });

  if (stops.length === 0) {
    return null;
  }
  stops.sort(byVisitTime);
  routes.sort((routeA, routeB) => routeB.stopsHere - routeA.stopsHere);

  return {
    email: target,
    name,
    stops,
    routes,
    stopsServiced: stops.filter((stop) => stopDisplayState(stop) === 'done')
      .length,
    currentStop: stops.find((stop) => stop.state === 'current') || null,
  };
}
